import React, { useState } from 'react';
import {
  Plus,
  Download,
  Share2,
  Filter,
  SortAsc,
  RefreshCw,
  Bookmark,
  Bell,
  Zap,
} from 'lucide-react';

interface QuickAction {
  id: string;
  label: string;
  icon: React.ReactNode;
  action: () => void;
  shortcut?: string;
  variant?: 'primary' | 'secondary';
}

interface QuickActionsProps {
  onRefresh?: () => void;
  onExport?: () => void;
  onShare?: () => void;
  onFilter?: () => void;
  onSort?: () => void;
}

export const QuickActions: React.FC<QuickActionsProps> = ({
  onRefresh,
  onExport,
  onShare,
  onFilter,
  onSort,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const actions: QuickAction[] = [
    {
      id: 'refresh',
      label: 'Refresh',
      icon: <RefreshCw className="w-5 h-5" />,
      action: () => onRefresh?.(),
      shortcut: '⌘R',
    },
    {
      id: 'export',
      label: 'Export',
      icon: <Download className="w-5 h-5" />,
      action: () => onExport?.(),
      shortcut: '⌘E',
    },
    {
      id: 'share',
      label: 'Share',
      icon: <Share2 className="w-5 h-5" />,
      action: () => onShare?.(),
      shortcut: '⌘S',
    },
    {
      id: 'filter',
      label: 'Filter',
      icon: <Filter className="w-5 h-5" />,
      action: () => onFilter?.(),
      shortcut: '⌘F',
    },
    {
      id: 'sort',
      label: 'Sort',
      icon: <SortAsc className="w-5 h-5" />,
      action: () => onSort?.(),
    },
  ];

  return (
    <div className="fixed bottom-6 right-6 z-40 flex flex-col items-end gap-2">
      {/* Expanded actions */}
      {isExpanded && (
        <div className="flex flex-col gap-2 animate-in slide-in-from-bottom-2 fade-in duration-200">
          {actions.slice(1).map((action, index) => (
            <button
              key={action.id}
              onClick={() => {
                action.action();
                setIsExpanded(false);
              }}
              className="group flex items-center gap-3 glass apple-card-glass rounded-apple-xl px-4 py-3 
                       hover:bg-apple-blue/10 hover:scale-105 transition-all duration-200 shadow-apple-button
                       animate-in slide-in-from-bottom fade-in"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <span className="apple-callout text-system-label font-medium whitespace-nowrap">
                {action.label}
              </span>
              <div className="flex items-center justify-center w-10 h-10 rounded-apple-lg 
                            bg-system-fill group-hover:bg-apple-blue group-hover:text-white 
                            transition-colors duration-200">
                {action.icon}
              </div>
              {action.shortcut && (
                <kbd className="px-2 py-1 apple-caption2 text-system-label-tertiary bg-system-fill rounded-apple">
                  {action.shortcut}
                </kbd>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Main action button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`
          glass-heavy rounded-apple-2xl p-4 
          shadow-apple-modal hover:scale-110 transition-all duration-300
          ${isExpanded ? 'rotate-45' : 'rotate-0'}
        `}
      >
        <Zap className={`w-6 h-6 ${isExpanded ? 'text-apple-blue' : 'text-system-label'}`} />
      </button>

      {/* Hint label */}
      {!isExpanded && (
        <div className="absolute right-16 bottom-4 glass apple-card-glass rounded-apple-lg px-3 py-2
                      opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity">
          <p className="apple-caption1 text-system-label-secondary whitespace-nowrap">
            Quick Actions
          </p>
        </div>
      )}
    </div>
  );
};
